import React, { useState } from "react";
import {
  Select,
  Slider,
  InputNumber,
  Typography,
  Modal,
  Button,
  Switch
} from "antd";
import { InfoCircleOutlined, SettingOutlined, CloseOutlined, DownOutlined } from "@ant-design/icons";
import {
  AUTO_CODER_ROLES,
  AUTO_CODER_PARAMETER_LIMITS,
  applyModelToSettings,
  applyRoleToSettings,
  applyDeepResearchToggle,
  getModelsForMode,
} from "../pages/Auto-Coder/autoCoderConfig";
import "./AutoCoderSettingsPanel.scss";

const { Title, Paragraph } = Typography;
const PARAMETER_INFO = {
  temperature: {
    title: "Temperature",
    sections: [
      {
        heading: "Controls creativity of the AI response.",
        points: [
          "Lower values (0.1–0.5) → More focused, deterministic, precise answers",
          "Higher values (1.0–2.0) → More creative, exploratory, varied responses",
        ],
      },
      {
        heading: "Recommended",
        points: [
          "Debugging / Review → Lower temperature",
          "Brainstorming / Generation → Higher temperature",
        ],
      },
    ],
  },
  max_tokens: {
    title: "Max Tokens",
    sections: [
      {
        heading: "Controls the maximum length of the AI response.",
        points: [
          "Lower values → Shorter, faster responses",
          "Higher values → More detailed and longer outputs",
        ],
      },
      {
        heading: "Recommended",
        points: [
          "Small explanations → 500–2000",
          "Large code generation / analysis → 4000+",
        ],
      },
    ],
  },
};

const AutoCoderSettingsPanel = ({ settings, onChange, disabled,isExpanded, setIsExpanded }) => {
  // const [isExpanded, setIsExpanded] = useState(false);
  const [infoParam, setInfoParam] = useState(null);

  const handleModelChange = (modelId) => {
    onChange(applyModelToSettings(settings, modelId));
  };

  const handleRoleChange = (roleId) => {
    onChange(applyRoleToSettings(settings, roleId));
  };

  const handleParameterChange = (key, value) => {
    onChange({
      ...settings,
      parameters: {
        ...settings.parameters,
        [key]: value,
      },
    });
  };

  const isThinking = settings.deepResearch === true;

  return (
    <div
      className={`auto-coder-settings-panel ${
        isExpanded ? "auto-coder-settings-panel--expanded" : "auto-coder-settings-panel--collapsed"
      } ${isThinking ? "auto-coder-settings-panel--thinking" : ""}`}
    >
      <button
        type="button"
        className="auto-coder-settings-panel__trigger"
        onClick={() => setIsExpanded(true)}
      >
        <SettingOutlined />
        <span>Generation settings</span>
        {settings.deepResearch && (
          <span className="auto-coder-settings-panel__badge">Research</span>
        )}
        <DownOutlined className="auto-coder-settings-panel__trigger-icon" />
      </button>

      <div
        className={`auto-coder-settings-panel__card ${
          isThinking ? "auto-coder-settings-panel__card--thinking" : ""
        }`}
      >
        <div className="auto-coder-settings-panel__card-header">
          <span
            className="auto-coder-settings-panel__card-title"
            onClick={() => setIsExpanded(false)}
          >
            <SettingOutlined />
            <span>Generation settings</span>
            {isThinking && (
              <span className="auto-coder-settings-panel__header-thinking-indicator" />
            )}
          </span>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={() => setIsExpanded(false)}
            size="small"
          />
        </div>

        <div className="auto-coder-settings-panel__card-body">
          <div className="auto-coder-settings-panel__deep-research">
            <span className="auto-coder-settings-panel__label">Deep Research</span>
            <Switch
              checked={settings.deepResearch}
              onChange={(checked) => onChange(applyDeepResearchToggle(settings, checked))}
              disabled={disabled}
            />
          </div>

          <div className="auto-coder-settings-panel__field">
            <label className="auto-coder-settings-panel__label">Model</label>
            <Select
              value={settings.modelId}
              onChange={handleModelChange}
              options={getModelsForMode(settings.deepResearch).map((m) => ({
                value: m.id,
                label: m.label,
              }))}
              disabled={disabled}
              style={{ width: "100%" }}
            />
          </div>

          <div className="auto-coder-settings-panel__field">
            <label className="auto-coder-settings-panel__label">Role</label>
            <Select
              value={settings.roleId}
              onChange={handleRoleChange}
              options={AUTO_CODER_ROLES.map((r) => ({
                value: r.id,
                label: r.label,
              }))}
              disabled={disabled}
              style={{ width: "100%" }}
            />
          </div>

          <div className="auto-coder-settings-panel__params">
            <span className="auto-coder-settings-panel__label">Hyperparameters</span>

            {Object.entries(AUTO_CODER_PARAMETER_LIMITS).map(([key, limits]) => (
              <div key={key} className="auto-coder-settings-panel__param-row">
                <label className="auto-coder-settings-panel__param-label">
                  {key === "max_tokens" ? "Max tokens" : "Temperature"}

                  <Button
                    type="link"
                    icon={<InfoCircleOutlined />}
                    onClick={() => setInfoParam(key)}
                  />
                </label>

                <div className="auto-coder-settings-panel__param-controls">
                  <Slider
                    min={limits.min}
                    max={limits.max}
                    step={limits.step}
                    value={settings.parameters[key]}
                    onChange={(value) => handleParameterChange(key, value)}
                    disabled={disabled}
                  />
                  <InputNumber
                    min={limits.min}
                    max={limits.max}
                    step={limits.step}
                    value={settings.parameters[key]}
                    onChange={(value) => handleParameterChange(key, value)}
                    disabled={disabled}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Modal
        title={infoParam ? PARAMETER_INFO[infoParam].title : ""}
        open={!!infoParam}
        onCancel={() => setInfoParam(null)}
        footer={[
          <Button key="close" type="primary" onClick={() => setInfoParam(null)}>
            Close
          </Button>,
        ]}
      >
        {infoParam &&
          PARAMETER_INFO[infoParam].sections.map((section, idx) => (
            <div key={idx} style={{ marginBottom: "12px" }}>
              <Title level={5}>{section.heading}</Title>
              <ul style={{ paddingLeft: "20px" }}>
                {section.points.map((point, i) => (
                  <li key={i}>
                    <Paragraph style={{ margin: 0 }}>{point}</Paragraph>
                  </li>
                ))}
              </ul>
            </div>
          ))}
      </Modal>
    </div>
  );
};

export default AutoCoderSettingsPanel;
